import React, { useEffect, useMemo, useState } from 'react';
import './CetarusLoginAnimation.css';

const CetarusLoginAnimation = ({
  onAnimationComplete,
  showLoadingIndicator = false,
  loadingMessage = 'Loading...',
  backgroundVariant = 'transparent'
}) => {
  const text = 'CETARUS';

  const [displayText, setDisplayText] = useState('');
  const [isBlack, setIsBlack] = useState(false);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      setDisplayText(text);
      setIsBlack(true);
      const fallbackTimeout = setTimeout(() => {
        onAnimationComplete?.();
      }, 300);

      return () => {
        clearTimeout(fallbackTimeout);
      };
    }

    const typingDelay = 180;
    const blackPauseDuration = 1000; // 1 second
    let animationFinished = false;
    let cycleCount = 0;
    const timeouts = [];

    const schedule = (callback, delay) => {
      const timeoutId = setTimeout(() => {
        if (!animationFinished) {
          callback();
        }
      }, delay);
      timeouts.push(timeoutId);
    };

    const runCycle = (startDelay = 0) => {
      // Reset state for new cycle (schedule it to happen at cycle start)
      schedule(() => {
        if (!animationFinished) {
          setDisplayText('');
          setIsBlack(false);
        }
      }, startDelay);

      // Type each letter of "CETARUS"
      for (let i = 0; i < text.length; i += 1) {
        schedule(() => {
          if (!animationFinished) {
            setDisplayText(text.slice(0, i + 1));
            setIsBlack(false);
          }
        }, startDelay + typingDelay * (i + 1));
      }

      // After last letter, make text black for 1 second
      const blackStartTime = startDelay + typingDelay * text.length;
      schedule(() => {
        if (!animationFinished) {
          setIsBlack(true);
        }
      }, blackStartTime);

      // After black pause, notify parent on first cycle, then loop
      const cycleEndTime = blackStartTime + blackPauseDuration;
      schedule(() => {
        if (!animationFinished) {
          cycleCount += 1;
          
          // Notify parent after first cycle completes
          if (cycleCount === 1) {
            onAnimationComplete?.();
          }
          
          // Loop: start next cycle immediately
          runCycle(cycleEndTime);
        }
      }, cycleEndTime);
    };

    // Start first cycle
    runCycle(0);

    return () => {
      animationFinished = true;
      timeouts.forEach(clearTimeout);
    };
  }, [text, onAnimationComplete, prefersReducedMotion]);

  const overlayClassName = [
    'cetarus-animation-overlay',
    backgroundVariant === 'signin' ? 'cetarus-animation-overlay--signin' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={overlayClassName} aria-live="polite" role="status">
      <div className="cetarus-animation-text-wrapper">
        <span
          className={`cetarus-animation-text ${isBlack ? 'cetarus-animation-text--black' : ''}`}
        >
          {displayText}
        </span>
        {showLoadingIndicator && (
          <div className="cetarus-animation-loader">
            <span className="cetarus-animation-loader-spinner" aria-hidden="true" />
            <span className="cetarus-animation-loader-text">{loadingMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CetarusLoginAnimation;

